import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize, map, Observable, shareReplay } from 'rxjs';

import {
  MfaSetupDialogComponent,
  MfaSetupDialogResult
} from './mfa-setup-dialog.component';

/**
 * MfaEnrollmentService — orquesta el enrolamiento MFA de forma TRANSVERSAL.
 *
 * Espejo exacto de StepUpService. `ensureEnrollment()` abre el modal canónico
 * (MfaSetupDialogComponent), que llama a AuthService.enrollMfaStart/Confirm y
 * persiste los tokens reemitidos. Resuelve `true` si el usuario completó el
 * enrolamiento, `false` si canceló o falló.
 *
 * Existe para que el interceptor pueda resolver un 403 MFA_ENROLLMENT_REQUIRED
 * SIN navegar a la ruta /mfa-setup: esa navegación destruía el componente que
 * tenía el formulario recién llenado y descartaba la request original. Con el
 * modal, la pantalla del usuario queda en pie y la request se reintenta sola.
 *
 * SERIALIZACIÓN (anti-race de modales): replica el patrón `_stepUpInFlight$` de
 * StepUpService — observable en vuelo compartido + shareReplay(1) + finalize que
 * lo nulifica. Si N requests reciben 403 MFA_ENROLLMENT_REQUIRED casi
 * simultáneamente, TODAS comparten el MISMO modal. MatDialog por sí solo NO
 * previene instancias múltiples: esta caché es la que garantiza un único modal.
 */
@Injectable({ providedIn: 'root' })
export class MfaEnrollmentService {
  private readonly _dialog = inject(MatDialog);

  // Enrolamiento en vuelo compartido. Mismo contrato que _stepUpInFlight$.
  private _inFlight$: Observable<boolean> | null = null;

  /**
   * Garantiza que el usuario tenga MFA enrolado. Si ya hay un enrolamiento en
   * vuelo, devuelve el MISMO observable (un solo modal); si no, abre el modal y
   * cachea el flujo.
   */
  ensureEnrollment(): Observable<boolean> {
    if (this._inFlight$) {
      return this._inFlight$;
    }

    this._inFlight$ = this._dialog
      .open<MfaSetupDialogComponent, void, MfaSetupDialogResult>(
        MfaSetupDialogComponent,
        {
          autoFocus: false,
          disableClose: false,
          // Layout responsive del panel (definido en src/styles/styles.scss): el
          // panel se monta fuera del ViewEncapsulation del componente. Abajo de
          // 600px lo vuelve una hoja full-screen. Mismo panelClass que el
          // step-up, para que las dos hojas MFA se comporten igual en mobile.
          panelClass: 'mfa-dialog-panel'
        }
      )
      .afterClosed()
      .pipe(
        // afterClosed() emite { enrolled: true } al completar, o undefined si el
        // usuario cancela / cierra por backdrop o ESC: normalizamos a boolean.
        map(result => result?.enrolled === true),
        finalize(() => {
          this._inFlight$ = null;
        }),
        shareReplay(1)
      );

    return this._inFlight$;
  }
}
